/**
 * cleanupRogueOperationsKey.js
 *
 * FT-129: Remove rogue operations_<org>_<vessel> keys from localStorage
 *
 * PROBLEM: The MODULE_MAP had an 'operations' alias that pointed to the same
 * cloud collection as 'operations_main_logbook'. When useSyncBridge iterated
 * over MODULE_MAP and hydrated from cloud, both entries pulled the same data
 * but created DIFFERENT localStorage keys:
 *
 * - operations_<org>_<vessel> (ROGUE - 766KB wasted)
 * - operations_main_logbook_<org>_<vessel> (CORRECT)
 *
 * FIX: Removed the 'operations' alias from MODULE_MAP (same fix pattern as FT-88
 * seaready_checklist_records removal). This migration cleans up existing rogue keys.
 *
 * This is a ONE-TIME migration that:
 * 1. Scans localStorage for keys matching /^operations_[a-f0-9-]+_[a-f0-9-]+$/
 * 2. Deletes them (the data already exists in operations_main_logbook_* keys)
 * 3. Sets migration flag to prevent re-running
 */

const MIGRATION_FLAG = 'sr.migration.cleanupRogueOperationsKey_v1';

/**
 * Run cleanup for rogue operations_<org>_<vessel> keys
 * @returns {boolean} - True if migration ran, false if skipped
 */
export const cleanupRogueOperationsKey = () => {
  // Check if already migrated
  if (localStorage.getItem(MIGRATION_FLAG) === 'complete') {
    return false;
  }

  try {
    console.log('[FT-129] Starting rogue operations key cleanup...');

    let deletedCount = 0;
    let freedBytes = 0;

    // Pattern: operations_<org-uuid>_<vessel-uuid>
    // Must NOT match operations_main_logbook_, operations_voyages_, etc.
    const rogueKeyPattern = /^operations_[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}_/i;

    // Collect keys to delete (avoid modifying localStorage while iterating)
    const keysToDelete = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      // Match rogue pattern but NOT legitimate operations_* submodules
      if (rogueKeyPattern.test(key)) {
        // Double-check it's not a legitimate submodule key
        const legitimatePatterns = [
          'operations_main_logbook_',
          'operations_voyages_',
          'operations_checklists_',
          'operations_checklist_templates_',
          'operations_checklist_favourites_',
          'operations_permits_',
          'operations_bunkering_',
          'operations_voyage_templates_',
          'operations_daily_checks_config_'
        ];

        const isLegitimate = legitimatePatterns.some(pattern => key.startsWith(pattern));

        if (!isLegitimate) {
          keysToDelete.push(key);
        }
      }
    }

    // Delete rogue keys
    for (const key of keysToDelete) {
      const value = localStorage.getItem(key);
      const sizeKB = value ? (value.length / 1024).toFixed(1) : 0;
      freedBytes += value ? value.length : 0;

      console.log(`[FT-129] Deleting rogue key: ${key} (${sizeKB} KB)`);
      localStorage.removeItem(key);
      deletedCount++;
    }

    // Mark migration complete
    localStorage.setItem(MIGRATION_FLAG, 'complete');

    if (deletedCount > 0) {
      const freedKB = (freedBytes / 1024).toFixed(1);
      console.log(`[FT-129] ✅ Cleanup complete: ${deletedCount} rogue key(s) deleted, ${freedKB} KB freed`);
    } else {
      console.log('[FT-129] ✅ No rogue operations keys found');
    }

    return true;

  } catch (error) {
    console.error('[FT-129] ❌ Cleanup failed:', error);
    // Still set flag - we don't want to keep retrying if there's an error
    localStorage.setItem(MIGRATION_FLAG, 'complete');
    return false;
  }
};
